import { useState } from 'react';
import type { DevListingView } from '@dunea/shared';
import { apiCreateDevListing } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import { BTN_GHOST, CARD, ErrorBanner, Spinner } from '~/components/workbench/tools/payments/ui';
import { apiErrorMessage, checkManifestText, type ManifestCheck } from '~/components/marketplace/developer-model';
import { ServerCheck } from './ServerCheck';
import { ManifestInput, SecretReveal } from './shared';

/** Paste/upload a manifest → validate → create the listing → show its client secret once. */
export function NewListing({
  onCreated,
  onCancel,
}: {
  onCreated: (listing: DevListingView) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const [check, setCheck] = useState<ManifestCheck>(() => checkManifestText(''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  const submit = async () => {
    if (!check.ok) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const { listing, clientSecret } = await apiCreateDevListing(check.raw);
      onCreated(listing);
      setSecret(clientSecret);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create the listing.'));
    } finally {
      setBusy(false);
    }
  };

  if (secret) {
    return (
      <div className={classNames(CARD, 'space-y-3 p-4')}>
        <p className="text-[12.5px] text-primary">
          Listing created as a draft. Submit its first version for review from the list below.
        </p>
        <SecretReveal
          label="DUNEA_CLIENT_SECRET"
          value={secret}
          note="Your service uses it to verify Dunea's signed webhooks and tool calls."
          onDone={onCancel}
        />
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className={classNames(CARD, 'space-y-3 p-4')}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-medium text-primary">New listing</h3>
        <button type="button" onClick={onCancel} disabled={busy} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
      <ManifestInput
        value={text}
        onChange={(next, nextCheck) => {
          setText(next);
          setCheck(nextCheck);
        }}
        disabled={busy}
      />
      <ServerCheck raw={check.raw} disabled={busy} />
      {error && <ErrorBanner message={error} />}
      <button type="submit" disabled={busy || !check.ok} className="ui-btn-accent disabled:opacity-50">
        {busy && <Spinner />}
        Create listing
      </button>
    </form>
  );
}
